import cx from 'classnames'
import dayjs from 'dayjs'
import { Text } from 'blaze-ui/Text/Text'
import { Check2 } from 'blaze-ui/icons'
import { CoverImage } from 'screens/ContentPlan/CoverImage/CoverImage'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { CampaignOption } from 'types'
import Styles from './CampaignDropdown.module.scss'

interface CampaignDropdownProps {
  schedules: CampaignOption[]
  selectedScheduleId: number | undefined
  onSelect: (scheduleId: number) => void
  isLoading?: boolean
}

const SkeletonRow = () => (
  <div className={Styles.row} aria-hidden="true">
    <LoadingShimmerer className={Styles.skeletonThumbnail} borderRadius={4} />
    <div className={Styles.info}>
      <LoadingShimmerer className={Styles.skeletonTitle} borderRadius={4} />
      <LoadingShimmerer className={Styles.skeletonMeta} borderRadius={4} />
    </div>
  </div>
)

export const CampaignDropdown = ({ schedules, selectedScheduleId, onSelect, isLoading }: CampaignDropdownProps) => {
  const showSkeletons = isLoading && schedules.length === 0

  return (
    <div className={Styles.container}>
      <div className={Styles.list}>
        {showSkeletons && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}
        {!showSkeletons &&
          schedules.map((schedule) => {
            const isSelected = schedule.id === selectedScheduleId
            const start = dayjs(schedule.start_date).format('MMM D')
            const end = dayjs(schedule.end_date).format('MMM D')

            return (
              <button
                key={schedule.id}
                type="button"
                className={cx(Styles.row, { [Styles.selected]: isSelected })}
                onClick={() => onSelect(schedule.id)}
              >
                {schedule.cover_image ? (
                  <div className={Styles.thumbnail}>
                    <CoverImage src={schedule.cover_image} alt={schedule.title ?? ''} width={50} height={50} />
                  </div>
                ) : (
                  <div className={Styles.thumbnailPlaceholder} />
                )}
                <div className={Styles.info}>
                  <Text color="var(--dark-90)" fontWeight={isSelected ? '500' : undefined}>
                    {schedule.title ?? 'Untitled Campaign'}
                  </Text>
                  <Text variant="secondary" color="var(--dark-60)">
                    {start} <span className={Styles.dateDash}>– </span>
                    {end}
                  </Text>
                </div>
                {isSelected && (
                  <div className={Styles.checkmark} aria-hidden="true">
                    <Check2 size={10} color="var(--light-100)" />
                  </div>
                )}
              </button>
            )
          })}
      </div>
    </div>
  )
}
